import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SignaturePad } from "../components/SignaturePad";

export default function PublicScreeningPage() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [responses, setResponses] = useState({});
  const [attest, setAttest] = useState({ first_name: "", last_name: "", employee_id: "", email: "" });
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/screenings/public/${token}`).then((r) => setForm(r.data)).catch(() => setNotFound(true));
  }, [token]);

  const toggle = (fid, opt) => {
    setResponses((r) => {
      const cur = new Set(r[fid] || []);
      if (cur.has(opt)) cur.delete(opt); else cur.add(opt);
      return { ...r, [fid]: Array.from(cur) };
    });
  };

  const submit = async () => {
    if (!attest.first_name.trim() || !attest.last_name.trim() || !attest.email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }
    if (!attest.employee_id.trim()) { toast.error("Please enter your employee ID"); return; }
    if (!sig) { toast.error("Please sign before submitting"); return; }
    setBusy(true);
    try {
      await api.post(`/screenings/public/${token}/submit`, { ...attest, signature_png: sig, responses });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-background">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-2 px-6">
          <img src="/shc-logo.png" alt="Silver Home Care" className="h-9 w-auto" />
          <span className="font-heading text-lg font-semibold tracking-tight text-stone-900">Silver Home Care <span className="font-normal text-stone-500">Employee Screening</span></span>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-10">{children}</main>
    </div>
  );

  if (notFound) {
    return shell(
      <div className="rounded-lg border border-stone-200 bg-white p-10 text-center shadow-sm" data-testid="screening-not-found">
        <p className="font-heading text-xl font-semibold text-stone-900">Form not found</p>
        <p className="mt-2 text-sm text-stone-500">This link may have been deactivated. Contact the office for a current link.</p>
      </div>
    );
  }
  if (!form) {
    return shell(<div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>);
  }
  if (done) {
    return shell(
      <div className="rounded-lg border border-[#27AE60]/30 bg-[#27AE60]/5 p-10 text-center shadow-sm" data-testid="screening-done">
        <CheckCircle size={44} weight="fill" className="mx-auto text-[#27AE60]" />
        <h1 className="mt-3 font-heading text-3xl font-semibold text-stone-900">Screening submitted</h1>
        <p className="mt-2 text-sm text-stone-600">Thank you — your signed screening has been filed to your employee record.</p>
      </div>
    );
  }

  return shell(
    <div className="space-y-6" data-testid="public-screening-page">
      <div>
        <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">{form.title}</h1>
        {form.description && <p className="mt-2 text-sm italic text-stone-600">{form.description}</p>}
      </div>

      {form.fields.map((f) => (
        <div key={f.id} className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid={`screening-field-${f.id}`}>
          <p className="text-sm font-medium text-stone-900">{f.label}</p>
          {f.type === "checkboxes" && (
            <div className="mt-3 space-y-2">
              {f.options.map((opt) => {
                const checked = (responses[f.id] || []).includes(opt);
                return (
                  <label key={opt} className="flex cursor-pointer items-start gap-2.5 text-sm text-stone-700" data-testid={`screening-opt-${f.id}-${opt.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(f.id, opt)} className="mt-0.5 h-4 w-4 accent-[#2D6A4F]" />
                    <span>{opt}</span>
                  </label>
                );
              })}
            </div>
          )}
          {f.type === "date" && (
            <Input type="date" className="mt-3 max-w-xs" value={responses[f.id] || ""}
              onChange={(e) => setResponses((r) => ({ ...r, [f.id]: e.target.value }))} data-testid={`screening-date-${f.id}`} />
          )}
          {f.type === "text" && (
            <Input className="mt-3" value={responses[f.id] || ""}
              onChange={(e) => setResponses((r) => ({ ...r, [f.id]: e.target.value }))} data-testid={`screening-text-${f.id}`} />
          )}
        </div>
      ))}

      <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="screening-attest">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="scr-first">First name</Label>
            <Input id="scr-first" value={attest.first_name} onChange={(e) => setAttest((a) => ({ ...a, first_name: e.target.value }))} data-testid="screening-first-name" />
          </div>
          <div>
            <Label htmlFor="scr-last">Last name</Label>
            <Input id="scr-last" value={attest.last_name} onChange={(e) => setAttest((a) => ({ ...a, last_name: e.target.value }))} data-testid="screening-last-name" />
          </div>
          <div>
            <Label htmlFor="scr-eid">Employee ID#</Label>
            <Input id="scr-eid" value={attest.employee_id} onChange={(e) => setAttest((a) => ({ ...a, employee_id: e.target.value }))} data-testid="screening-employee-id" />
          </div>
          <div>
            <Label htmlFor="scr-email">Email</Label>
            <Input id="scr-email" type="email" value={attest.email} onChange={(e) => setAttest((a) => ({ ...a, email: e.target.value }))} data-testid="screening-email" />
          </div>
        </div>
        <div className="mt-4">
          <Label>Signature</Label>
          <SignaturePad onChange={setSig} />
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={submit} disabled={busy} data-testid="screening-submit">{busy ? "Submitting..." : "Submit"}</Button>
        </div>
      </div>
    </div>
  );
}
